import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { InlineDrawerFrame } from 'views/Agent/form-frame';
import { Drawer, Button, Form, message } from 'antd';
import { css } from '@emotion/css';
import { Formik } from 'formik';
import ConfigurationPanel from './Configuration';
import useResult from './useResult';
import { useKnowledgeConfiguration } from './atom/knowledgeConfiguration';
import { useCommonKnowledgeMutate } from './hook';
import { useTranslate } from '@tolgee/react';
import { useMemo, useState } from 'react';
import type { Store } from 'components/StoreScopes';
import {
  combineStoreScopes,
  defaultValueMap,
  formValue2knowledgeMap,
  knowledge2formValueMap,
  knowledgeTypeStepMap,
  UnConfigStore,
} from 'views/BootGuide/views/AiModel/share';
import { useStores } from '@knowledge/common/cache/store';
import { useStoreMap } from 'components/StoreScopes';
import { supportKnowledgeStoresFilter } from '../utils';
import { useLLM } from '@knowledge/common/hooks/useGroup';

type EditDrawerProps = {
  type: Knowledge.KnowledgeType;
  agentEntry?: {
    knowledge: Knowledge.FormValue;
    stores: Store[];
    edit: boolean;
    presentation?: 'sidebar' | 'business';
    onClose(): void;
  };
};

function EditDrawer(props: EditDrawerProps) {
  const { type } = props;
  const { t } = useTranslate();
  const Frame = props.agentEntry?.presentation === 'sidebar' ? InlineDrawerFrame : Drawer;
  const LLM = useLLM();
  const configuration = useKnowledgeConfiguration();
  const [agentEntries, setAgentEntries] = useState(() =>
    props.agentEntry
      ? [
          {
            visible: true,
            mode: props.agentEntry.edit ? 'edit' : 'add',
            knowledge: props.agentEntry.knowledge,
            storeDataSource: props.agentEntry.stores,
            id: 'agent-knowledge',
            drawerIndex: 0,
          },
        ]
      : [],
  );
  const kc = props.agentEntry ? agentEntries : configuration.kc;
  const close = () => {
    if (props.agentEntry) {
      setAgentEntries([]);
      props.agentEntry.onClose();
    } else configuration.close();
  };
  const { checkKnowledge, loading, createKnowledge, editKnowledge } = useCommonKnowledgeMutate();
  const [openResult, closeResult, modalInstance] = useResult(type, loading);

  const { data: _stores } = useStores();
  const stores = useMemo(() => supportKnowledgeStoresFilter(_stores || []), [_stores]);
  const storeMap = useStoreMap({
    list: (stores || []) as UnConfigStore[],
  });

  const handleSubmit = async (value: Knowledge.FormValue) => {
    const { conditions, adaptValue, id } = value as any;
    const _contentValue = formValue2knowledgeMap[type](adaptValue);
    const { templateCode, content } = _contentValue || {};
    const contentValue = LLM ? _contentValue : { templateCode, content };
    const mutateFn = id ? editKnowledge : createKnowledge;
    try {
      const checkResult = await checkKnowledge({
        ...conditions[0],
        ...contentValue,
        definitionCode: type,
      });
      if (checkResult?.length === 0) {
        try {
          await mutateFn({
            ...conditions[0],
            ...contentValue,
            definitionCode: type,
            overwrite: [],
            keep: [],
            ...(id ? { id } : {}),
          });
          close();
          message.success(t('operation.saveSucceed'));
        } catch (e) {
          message.error(describeKnowledgeApiError(e, undefined, t('operation.saveFailed')));
        }
      } else {
        openResult(
          checkResult,
          contentValue,
          async (overwriteAndKeep: {
            keep: Knowledge.KnowledgeItem[];
            overwrite: Knowledge.KnowledgeItem[];
          }) => {
            try {
              await mutateFn({
                ...conditions[0],
                ...contentValue,
                definitionCode: type,
                ...overwriteAndKeep,
                ...(id ? { id } : {}),
              });
              closeResult();
              close();
              message.success(t('operation.saveSucceed'));
            } catch (e) {
              message.error(describeKnowledgeApiError(e, undefined, t('operation.saveFailed')));
            }
          },
        );
      }
    } catch (e) {
      message.error(describeKnowledgeApiError(e, undefined, t('operation.saveFailed')));
    }
  };

  const values = useMemo(() => {
    return kc.map((config) => {
      const {
        knowledge: {
          conditions,
          contentValue: { content, templateCode },
        },
      } = config;
      const adaptValue =
        knowledge2formValueMap[type](
          combineStoreScopes(conditions.map((condition) => condition.storeScope.storeScope)),
          { templateCode: templateCode || '', value: content },
        ) || defaultValueMap[knowledgeTypeStepMap[type]];
      return {
        ...config,
        knowledge: {
          ...config.knowledge,
          adaptValue,
        },
      };
    });
  }, [kc, type]);

  return (
    <>
      {values.map((config, index) => {
        return (
          <Formik initialValues={config.knowledge} onSubmit={handleSubmit} key={index}>
            {({ handleSubmit }) => {
              return (
                <Form data-agent-form>
                  <Frame
                    visible={config.visible}
                    maskClosable={false}
                    onClose={close}
                    width={1200}
                    title={config.mode === 'add' ? t('marketing.新增') : t('marketing.编辑')}
                    closable
                    push={false}
                    footer={
                      <>
                        <Button
                          data-agent-effect="commit"
                          type="primary"
                          className={mr16}
                          onClick={() => handleSubmit()}
                          loading={loading}
                        >
                          {t('phrase.保存')}
                        </Button>
                        <Button data-agent-action="close" onClick={close}>
                          {t('phrase.取消')}
                        </Button>
                      </>
                    }
                  >
                    <ConfigurationPanel
                      drawerIndex={config.drawerIndex}
                      type={type}
                      stores={config.storeDataSource}
                      storeMap={storeMap}
                    />
                    {modalInstance}
                  </Frame>
                </Form>
              );
            }}
          </Formik>
        );
      })}
    </>
  );
}

const mr16 = css`
  margin-right: 16px;
`;

export default EditDrawer;
